import { __makeTemplateObject, __read } from "tslib";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Box, Button, createStyles, makeStyles } from "@material-ui/core";
import ContentEditable from "react-contenteditable";
import styled from "styled-components";
import HotspotHighlightSVG from "../../assets/icons/icon-hotspot-highlight.svg";
import { TransparentButton } from "../../ui/Buttons/styles";
var useStyles = makeStyles(function (theme) {
    return createStyles({
        editable: {
            fontSize: "14px",
            color: function (_a) {
                var student = _a.student;
                return "".concat(student ? theme.palette.colors.info[750] : theme.palette.text.primary);
            },
            padding: "8px",
            minHeight: "60px",
            overflowWrap: "anywhere",
            textAlign: "left",
            fontFamily: "var(--openSans)",
            "& .highlight": {
                background: "hsl(50,100%,86%)",
                color: "hsl(224,7%,32%)",
                padding: "4px",
                borderRadius: "3px",
                "& .remove": {
                    display: "inline-block",
                    borderRadius: "50%",
                    height: "16px",
                    width: "16px",
                    color: "#fff",
                    background: "hsl(29,85%,51%)",
                    marginLeft: "3px",
                    cursor: "pointer",
                    lineHeight: "16px",
                    textAlign: "center"
                }
            }
        },
        highlightIcon: {
            cursor: "pointer"
        }
    });
});
var StyledContentEditable = styled(ContentEditable)(templateObject_1 || (templateObject_1 = __makeTemplateObject(["\n\tborder: 1px solid rgb(151, 151, 151);\n\tborder-radius: 4px;\n"], ["\n\tborder: 1px solid rgb(151, 151, 151);\n\tborder-radius: 4px;\n"])));
var StyledMatButton = styled(Button)(templateObject_2 || (templateObject_2 = __makeTemplateObject(["\n\tpadding: 10px;\n\tbackground: ", ";\n\tmin-width: 32px;\n\tcolor: ", ";\n\t&:hover {\n\t\tbackground: hsl(240, 6%, 97%);\n\t}\n"], ["\n\tpadding: 10px;\n\tbackground: ", ";\n\tmin-width: 32px;\n\tcolor: ", ";\n\t&:hover {\n\t\tbackground: hsl(240, 6%, 97%);\n\t}\n"])), function (props) { return props.theme.palette.colors.basic[150]; }, function (props) { return props.theme.palette.colors.basic[750]; });
var HotspotHighlightInput = function (_a) {
    var value = _a.value, onChange = _a.onChange, readonly = _a.readonly, student = _a.student, _b = _a.currentIndex, currentIndex = _b === void 0 ? 0 : _b;
    var classes = useStyles({ student: student });
    var contentRef = useRef(null);
    var _c = __read(useState(), 2), position = _c[0], setPosition = _c[1];
    var fixContent = function (container, level) {
        if (level === void 0) { level = 0; }
        container.childNodes.forEach(function (node) {
            var _a;
            var newText = document.createTextNode(node.innerText);
            if (level >= 3)
                container.replaceChild(newText, node);
            else if (level === 2 && node.nodeName !== "#text")
                container.replaceChild(newText, node);
            else if (level === 1 && node.className !== "remove" && node.nodeName !== "#text") {
                (_a = node.querySelector(".remove")) === null || _a === void 0 ? void 0 : _a.remove();
                container.replaceChild(newText, node);
            }
            else if (level === 0 && node.className !== "highlight" && node.nodeName !== "#text") {
                container.replaceChild(newText, node);
            }
            if (node.nodeName !== "#text") {
                fixContent(node, level + 1);
            }
        });
    };
    var handleChange = function (container) {
        var newValue = [];
        container.childNodes.forEach(function (node) {
            var highlighted = node.nodeName !== "#text";
            newValue.push({
                highlighted: highlighted,
                text: highlighted ? node.childNodes[0].textContent : node.textContent
            });
        });
        onChange(newValue
            .filter(function (_a) {
            var text = _a.text;
            return text !== "";
        })
            .reduce(function (acc, item) {
            if (acc.length === 0)
                return [item];
            var lastAccItem = acc[acc.length - 1];
            if (lastAccItem.highlighted === item.highlighted) {
                lastAccItem.text += item.text;
            }
            else {
                acc.push(item);
            }
            return acc;
        }, []));
    };
    var handleSelect = function () {
        var _a;
        var fragment = position === null || position === void 0 ? void 0 : position.extractContents();
        if (!fragment || ((_a = fragment.parentElement) === null || _a === void 0 ? void 0 : _a.className) === "highlight")
            return;
        position === null || position === void 0 ? void 0 : position.deleteContents();
        fragment.childNodes.forEach(function (node) {
            var wrapper = document.createElement("span");
            wrapper.className = "highlight";
            wrapper.innerHTML = node.textContent + "<span class=\"remove\" id=\"".concat(currentIndex, "\">x</span>");
            node.replaceWith(wrapper);
        });
        position === null || position === void 0 ? void 0 : position.insertNode(fragment);
        fixContent(contentRef.current);
        handleChange(contentRef.current);
    };
    var removeHandler = useCallback(function (e) {
        var _a, _b;
        var target = e.target;
        if (target.className === "remove" && (target === null || target === void 0 ? void 0 : target.id) == (currentIndex === null || currentIndex === void 0 ? void 0 : currentIndex.toString())) {
            (_a = target.parentElement) === null || _a === void 0 ? void 0 : _a.replaceWith((_b = target.parentElement.firstChild) !== null && _b !== void 0 ? _b : "");
            handleChange(contentRef.current);
        }
    }, []);
    var keyPressHandler = useCallback(function (e) {
        var selection = window.getSelection();
        var parent = selection === null || selection === void 0 ? void 0 : selection.getRangeAt(0).startContainer.parentElement;
        if ((parent === null || parent === void 0 ? void 0 : parent.className) === "remove" &&
            contentRef.current &&
            !["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight"].includes(e.code)) {
            e.preventDefault();
        }
    }, []);
    useEffect(function () {
        document.addEventListener("click", removeHandler);
        document.addEventListener("keydown", keyPressHandler);
        return function () {
            document.removeEventListener("click", removeHandler);
            document.removeEventListener("keydown", keyPressHandler);
        };
    }, []);
    var handlePosition = function (newPosition) {
        setPosition(newPosition);
    };
    var html = value
        .map(function (_a) {
        var highlighted = _a.highlighted, text = _a.text;
        return highlighted ? "<span class=\"highlight\">".concat(text, "<span class=\"remove\" id=\"").concat(currentIndex, "\">x</span></span>") : text;
    })
        .join(" ");
    return (React.createElement(React.Fragment, null,
        React.createElement(StyledContentEditable, { className: classes.editable, innerRef: contentRef, disabled: readonly, html: html, onChange: function () {
                fixContent(contentRef.current);
                handleChange(contentRef.current);
            }, onBlur: function () {
                var selection = window.getSelection && window.getSelection();
                if (selection && selection.rangeCount > 0) {
                    handlePosition(selection.getRangeAt(0));
                }
            } }),
        React.createElement(Box, { display: "flex", justifyContent: "flex-end", mt: 1 }, student ? (React.createElement(StyledMatButton, { variant: "contained", onClick: handleSelect }, "Highlight")) : (React.createElement(TransparentButton, { onClick: handleSelect },
            React.createElement("img", { alt: "hotspot icon", src: HotspotHighlightSVG }))))));
};
export default HotspotHighlightInput;
var templateObject_1, templateObject_2;
//# sourceMappingURL=HotspotHighlightInput.js.map